import { ReactNode } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { clearDeviceTrust } from "@/lib/trustedDevice";
import { leaveWithoutGuard } from "@/lib/navigationGuardRegistry";
import { forgetAllAttempts } from "@/lib/jobPrivateMessages";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { usePortalRole, PortalRole } from "@/hooks/usePortalRole";
import OnboardingLauncher from "@/components/onboarding/OnboardingLauncher";
import InstallerSuspendedNotice from "@/components/portal/installer/InstallerSuspendedNotice";
import {
  Clock,
  LayoutDashboard,
  Users,
  Briefcase,
  LogOut,
  Menu,
  X,
  FileText,
  UserCog,
  ListChecks,
  HardHat,
  PoundSterling,
  Package,
  BarChart3,
  ClipboardList,
  Import,
  Filter,
  GraduationCap,
  Zap,
  Mail,
  Presentation,
  BookOpen,
  CalendarDays,
  User,
  MapPin,
  Shield,
  TrendingUp,
  DoorOpen,
  Banknote,
  MessageSquare,
  Megaphone,
  Headphones,
  Hammer,
  Ban,
  ShieldAlert,
  Star,
} from "lucide-react";
import { useState } from "react";
import logo from "@/assets/evohome-logo.png";
import PortalNotificationsPanel from "./PortalNotificationsPanel";
import RoleSwitcher from "./RoleSwitcher";
import ViewAsBanner from "./ViewAsBanner";
import CommandPalette from "./CommandPalette";
import PortalContent from "./PortalContent";
import EvoHomeFamilySwitcher from "@/components/family/EvoHomeFamilySwitcher";
import { MoreHorizontal, HelpCircle, Activity } from "lucide-react";

interface PortalLayoutProps {
  children: ReactNode;
  title?: string;
}

const roleLabels: Record<NonNullable<PortalRole>, string> = {
  admin: "Admin",
  consultant: "Consultant",
  installer: "Installer",
  customer: "Customer",
  partner: "Partner",
  marketing_manager: "Marketing",
  introducer: "Introducer",
  introducer_manager: "Introducer Manager",
  sales_manager: "Sales",
  customer_service: "Customer Service",
  surveyor: "Surveyor",
};

const roleBadgeColors: Record<NonNullable<PortalRole>, string> = {
  admin: "bg-destructive/10 text-destructive",
  consultant: "bg-primary/10 text-primary",
  installer: "bg-accent/10 text-accent",
  customer: "bg-success-muted text-success-text",
  partner: "bg-info-muted text-info-text",
  marketing_manager: "bg-accent/10 text-accent",
  introducer: "bg-warning-muted text-warning-text",
  introducer_manager: "bg-warning-muted text-warning-text",
  sales_manager: "bg-primary/10 text-primary",
  customer_service: "bg-info-muted text-info-text",
  surveyor: "bg-primary/10 text-primary",
};

function getNavItems(role: PortalRole) {
  switch (role) {
    case "admin":
      return [
        { label: "Dashboard", href: "/portal/admin", icon: LayoutDashboard },
        { label: "Leads", href: "/portal/admin/leads", icon: Users },
        { label: "Customers", href: "/portal/admin/customers", icon: User },
        { label: "Homeowner Accounts", href: "/portal/admin/planning-homeowners", icon: ClipboardList },
        { label: "Jobs", href: "/portal/admin/jobs", icon: Briefcase },
        { label: "Installations", href: "/portal/installations", icon: Hammer },
        { label: "Appointments", href: "/portal/admin/appointments", icon: CalendarDays },
        { label: "Consultants", href: "/portal/admin/consultants", icon: UserCog },
        { label: "Introducer Manager", href: "/portal/introducer-manager", icon: ShieldAlert },
        { label: "Installers", href: "/portal/admin/installers", icon: HardHat },
        { label: "Comms", href: "/portal/admin/comms", icon: MessageSquare },
        { label: "Content Hub", href: "/portal/admin/content-hub", icon: Megaphone },
        { label: "Territory", href: "/portal/admin/territory-control", icon: MapPin },
        { label: "Automation", href: "/portal/admin/automation", icon: Mail },
        { label: "Payouts", href: "/portal/admin/payouts", icon: PoundSterling },
        { label: "Compliance", href: "/portal/admin/compliance", icon: Shield },
        { label: "Pricing", href: "/portal/admin/pricing-governance", icon: PoundSterling },
        { label: "Aftercare", href: "/portal/admin/aftercare", icon: Shield },
        { label: "Data Cleanup", href: "/portal/admin/data-cleanup", icon: Filter },
      ];
    case "consultant":
      return [
        { label: "Dashboard", href: "/portal/consultant", icon: LayoutDashboard },
        { label: "My Schedule", href: "/portal/consultant/appointments", icon: CalendarDays },
        { label: "Jobs", href: "/portal/consultant/jobs", icon: Briefcase },
        { label: "Tasks", href: "/portal/consultant/tasks", icon: ListChecks },
        { label: "Design Lab", href: "/portal/consultant/toolkit", icon: Presentation },
        { label: "Presentation Deck", href: "/portal/consultant/sales-toolkit", icon: BookOpen },
        { label: "Visit briefing", href: "/portal/consultant/prep", icon: ClipboardList },
        { label: "Training", href: "/portal/consultant/training", icon: GraduationCap },
        { label: "Knowledge", href: "/portal/knowledge", icon: BookOpen },
      ];
    case "marketing_manager":
      return [
        { label: "Dashboard", href: "/portal/marketing", icon: BarChart3 },
        { label: "Contacts", href: "/portal/marketing/contacts", icon: Users },
        { label: "Tasks", href: "/portal/marketing/tasks", icon: ClipboardList },
        { label: "Appointments", href: "/portal/appointments", icon: CalendarDays },
        { label: "Import contacts", href: "/portal/marketing/import", icon: Import },
        { label: "Upload leads", href: "/portal/marketing/lead-upload", icon: Users },
        { label: "Triage", href: "/portal/marketing/triage", icon: Filter },
      ];
    case "partner":
      return [
        { label: "Dashboard", href: "/portal/partner", icon: LayoutDashboard },
      ];
    case "introducer":
      return [
        { label: "Dashboard", href: "/portal/introducer", icon: LayoutDashboard },
        { label: "My Leads", href: "/portal/introducer/leads", icon: Users },
        { label: "Rules & Scripts", href: "/portal/introducer/rules", icon: BookOpen },
        { label: "Rewards & Payments", href: "/portal/introducer/earnings", icon: PoundSterling },
        { label: "My Territory", href: "/portal/introducer/territory", icon: MapPin },
        { label: "My Metrics", href: "/portal/introducer/metrics", icon: Activity },
        { label: "Messages", href: "/portal/introducer/messages", icon: MessageSquare },
        // Availability hidden pre-launch — route remains live at /portal/introducer/availability.
        // EvoHome Improvements confirms working areas and availability with introducers directly for v1.
      ];
    case "installer":
      return [
        { label: "Jobs", href: "/portal/installer", icon: Briefcase },
        { label: "Schedule", href: "/portal/installer/schedule", icon: CalendarDays },
        { label: "Quotes", href: "/portal/installer/quotes", icon: FileText },
        { label: "Rates", href: "/portal/installer/rates", icon: PoundSterling },
        { label: "Compliance", href: "/portal/installer/compliance", icon: Shield },
        { label: "Reviews", href: "/portal/installer/reviews", icon: Star },
        { label: "Availability", href: "/portal/installer/availability", icon: Clock },
        { label: "Knowledge", href: "/portal/knowledge", icon: BookOpen },
        { label: "Profile", href: "/portal/installer/profile", icon: User },
        { label: "Help & Tutorials", href: "/portal/installer/help", icon: HelpCircle },
      ];
    case "sales_manager":
      return [
        { label: "Dashboard", href: "/portal/sales", icon: LayoutDashboard },
        { label: "Quote pack review", href: "/portal/reviews/quote-packs", icon: ClipboardList },
        { label: "Appointments", href: "/portal/appointments", icon: CalendarDays },
        { label: "Installations", href: "/portal/installations", icon: Hammer },
        { label: "Help", href: "/portal/sales/help", icon: HelpCircle },
      ];
    case "customer_service":
      return [
        { label: "Dashboard", href: "/portal/customer-service", icon: Headphones },
        { label: "Quote pack review", href: "/portal/reviews/quote-packs", icon: ClipboardList },
        { label: "Customers", href: "/portal/customer-service/customers", icon: Users },
        { label: "Appointments", href: "/portal/appointments", icon: CalendarDays },
        { label: "Installations", href: "/portal/installations", icon: Hammer },
        { label: "Help", href: "/portal/customer-service/help", icon: HelpCircle },
      ];
    case "introducer_manager":
      return [
        { label: "Dashboard", href: "/portal/introducer-manager", icon: LayoutDashboard },
        { label: "Introducer Leads", href: "/portal/introducer-manager/leads", icon: ClipboardList },
        { label: "Follow-ups", href: "/portal/introducer-manager/follow-ups", icon: Clock },
        { label: "Applicants", href: "/portal/introducer-manager/applicants", icon: UserCog },
        { label: "Introducers", href: "/portal/introducer-manager/introducers", icon: Users },
        { label: "Work Plan", href: "/portal/introducer-manager/work-plan", icon: CalendarDays },
        { label: "Training", href: "/portal/introducer-manager/training", icon: GraduationCap },
        { label: "Canvassing", href: "/portal/introducer-manager/canvassing", icon: DoorOpen },
        { label: "Territories", href: "/portal/introducer-manager/territories", icon: MapPin },
        { label: "Do-not-knock register", href: "/portal/introducer-manager/dnc", icon: Ban },
        { label: "Issues", href: "/portal/introducer-manager/issues", icon: ShieldAlert },
        { label: "Performance", href: "/portal/introducer-manager/performance", icon: BarChart3 },
        { label: "Activity Log", href: "/portal/introducer-manager/activity-log", icon: Activity },
        { label: "Help", href: "/portal/introducer-manager/help", icon: HelpCircle },
      ];
    case "surveyor":
      return [
        { label: "Dashboard", href: "/portal/surveyor", icon: LayoutDashboard },
        { label: "Cases", href: "/portal/surveyor/cases", icon: ClipboardList },
        { label: "Payouts", href: "/portal/surveyor/payouts", icon: Banknote },
        { label: "Compliance", href: "/portal/surveyor/compliance", icon: Shield },
        { label: "Profile", href: "/portal/surveyor/profile", icon: User },
      ];
    default:
      return [];
  }
}

import AdminPortalShell from "@/components/admin-portal/AdminPortalShell";

export default function PortalLayout({ children, title }: PortalLayoutProps) {
  const { role, roles } = usePortalRole();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);

  const handleSignOut = async () => {
    if (signingOut) return;
    setSigningOut(true);
    let failure: string | null = null;
    clearDeviceTrust();
    try {
      const { error } = await supabase.auth.signOut({ scope: "local" });
      if (error) failure = error.message;
    } catch (thrown) {
      failure = thrown instanceof Error ? thrown.message : "Sign out did not complete.";
    }
    if (failure) {
      // Still signed in. Nothing held is thrown away, and the screen does not
      // pretend otherwise — unfinished work stays exactly where it was.
      setSigningOut(false);
      toast.error(
        "You are still signed in — signing out did not complete. Nothing you were working on has been lost. Please check your connection and try again."
      );
      return;
    }
    // Signed out for certain. Anything held for this account in this tab is
    // cleared with it: the next person at the device must not see private
    // words or pick up somebody else's unfinished attempt.
    forgetAllAttempts();
    leaveWithoutGuard(() => navigate("/", { replace: true }));
  };

  if (role === "admin") return <AdminPortalShell title={title}>{children}</AdminPortalShell>;

  const navItems = getNavItems(role);
  // Headings are presentation-only: the existing role-filtered destinations remain intact.
  const managerGroup = (href: string) => {
    if (href.endsWith("/help")) return "Help";
    if (/\/(applicants|introducers|work-plan|training)$/.test(href)) return "Team";
    if (/\/(canvassing|territories|dnc|issues)$/.test(href)) return "Fieldwork";
    if (/\/(performance|activity-log)$/.test(href)) return "Results";
    return "Today";
  };
  const renderLinks = (mobile: boolean) => navItems.map((item, index) => {
    const active = location.pathname === item.href;
    const group = role === "introducer_manager" ? managerGroup(item.href) : null;
    const previous = index > 0 && role === "introducer_manager" ? managerGroup(navItems[index - 1].href) : null;
    return (
      <div key={item.href + item.label}>
        {group && group !== previous && <p className="px-3 pt-4 pb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{group}</p>}
        <Link to={item.href} onClick={mobile ? () => setMobileOpen(false) : undefined}
          className={`flex items-center gap-3 px-3 ${mobile ? "py-3 min-h-[48px]" : "py-2.5 min-h-[44px]"} rounded-xl text-sm font-medium touch-manipulation ${active ? "bg-primary/10 text-primary" : "text-muted-foreground hover:text-foreground hover:bg-muted/50"}`}
          aria-current={active ? "page" : undefined}>
          <item.icon className="w-4 h-4 shrink-0" />{item.label}
        </Link>
      </div>
    );
  });

  return (
    <div className="min-h-screen bg-muted/30">
      {/* Top bar */}
      <header className="sticky top-0 z-40 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 pt-[env(safe-area-inset-top)]">
        <div className="flex h-14 sm:h-16 items-center px-3 sm:px-4 md:px-6 gap-2 sm:gap-3">
          <button
            className="md:hidden p-2 -ml-1 min-h-[44px] min-w-[44px] flex items-center justify-center touch-manipulation active:bg-muted/60 rounded-md"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileOpen}
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
          <Link to="/portal" className="flex items-center gap-2 shrink-0 touch-manipulation">
            <img src={logo} alt="EvoHome Improvements" className="h-7 sm:h-8 w-auto" loading="eager" />
            <span className="font-semibold text-foreground text-xs sm:text-sm tracking-tight hidden sm:inline">EvoHome Improvements</span>
          </Link>
          <RoleSwitcher />
          {role && roles.length < 2 && (
            <span className={`text-[10px] font-semibold uppercase tracking-widest px-2.5 py-0.5 rounded-full ${roleBadgeColors[role]}`}>
              {roleLabels[role]}
            </span>
          )}
          <div className="flex-1" />
          <EvoHomeFamilySwitcher compact className="hidden sm:inline-flex" />
          <CommandPalette />
          <PortalNotificationsPanel />
          <Button variant="ghost" size="sm" onClick={handleSignOut} disabled={signingOut} className="gap-2 text-muted-foreground hover:text-foreground min-h-[44px] touch-manipulation">
            <LogOut className="w-4 h-4" />
            <span className="hidden sm:inline">{signingOut ? "Signing out…" : "Sign Out"}</span>
          </Button>
        </div>
      </header>

      <ViewAsBanner />

      <div className="flex">
        {/* Sidebar — desktop & tablet (md+). Sticky so iPad scrolling never loses nav. */}
        <aside className="hidden md:flex w-52 lg:w-56 flex-col border-r bg-background sticky top-14 sm:top-16 self-start h-[calc(100vh-3.5rem)] sm:h-[calc(100vh-4rem)] overflow-y-auto p-3 lg:p-4 gap-0.5">
          {renderLinks(false)}
        </aside>

        {/* Mobile nav overlay */}
        {mobileOpen && (
          <div className="fixed inset-0 z-30 md:hidden" role="dialog" aria-modal="true" aria-label="Navigation menu">
            <div
              className="absolute inset-0 bg-black/50"
              onClick={() => setMobileOpen(false)}
              onKeyDown={(e) => e.key === "Escape" && setMobileOpen(false)}
              role="button"
              tabIndex={0}
              aria-label="Close menu"
            />
            <nav className="absolute left-0 top-14 sm:top-16 bottom-0 w-72 max-w-[85vw] bg-background border-r overflow-y-auto p-4 pb-20 flex flex-col gap-0.5 shadow-lg">
              {renderLinks(true)}
              <div className="mt-3 border-t pt-3">
                <EvoHomeFamilySwitcher className="w-full justify-start" />
              </div>
            </nav>
          </div>
        )}

        {/* Main content */}
        <main className="flex-1 min-w-0 w-full p-4 md:p-6 lg:p-8 pb-mobile-nav md:pb-8">
          {role === "installer" && <InstallerSuspendedNotice />}
          {role === "installer" && <OnboardingLauncher audience="installer" />}
          <PortalContent width="default" className="px-0">
            {title && <h1 className="text-lg sm:text-xl md:text-2xl font-bold text-foreground mb-6 md:mb-8">{title}</h1>}
            {children}
          </PortalContent>
        </main>
      </div>

      {/* Mobile bottom nav — all portal roles. Shows first 4 items + "More" when overflow. */}
      {role && navItems.length > 0 && (
        <nav className="fixed bottom-0 left-0 right-0 z-40 md:hidden bg-background border-t safe-area-bottom" aria-label="Quick navigation">
          <div className="flex justify-around items-center h-14 gap-1 px-1">
            {(navItems.length > 5 ? navItems.slice(0, 4) : navItems.slice(0, 5)).map((item) => {
              const active = location.pathname === item.href;
              return (
                <Link
                  key={item.href + item.label}
                  to={item.href}
                  className={`flex flex-col items-center justify-center gap-0.5 flex-1 h-full transition-colors min-w-0 px-1 ${
                    active ? "text-primary" : "text-muted-foreground"
                  }`}
                  aria-current={active ? "page" : undefined}
                  aria-label={item.label}
                >
                  <item.icon className="w-5 h-5" />
                  <span className="max-w-full truncate text-[10px] font-medium leading-none">{item.label}</span>
                </Link>
              );
            })}
            {navItems.length > 5 && (
              <button
                type="button"
                onClick={() => setMobileOpen(true)}
                className="flex flex-col items-center justify-center gap-0.5 flex-1 h-full transition-colors min-w-0 px-1 text-muted-foreground"
                aria-label="More navigation"
                aria-haspopup="dialog"
                aria-expanded={mobileOpen}
              >
                <MoreHorizontal className="w-5 h-5" />
                <span className="max-w-full truncate text-[10px] font-medium leading-none">More</span>
              </button>
            )}
          </div>
        </nav>
      )}
    </div>
  );
}
